import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuth } from '../store/auth.store';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuth();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Electric Wave Animation Logic
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let w: number, h: number;
    const particles: any[] = [];
    const waveCount = 3;
    
    const resize = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', resize);
    resize();

    class Wave {
      phase: number;
      offset: number;
      frequency: number;
      amplitude: number;
      speed: number;
      color: string;

      constructor(offset: number, color: string, amplitude: number, speed: number) {
        this.phase = 0;
        this.offset = offset;
        this.frequency = 0.001;
        this.amplitude = amplitude;
        this.speed = speed;
        this.color = color;
      }

      draw() {
        ctx!.beginPath();
        ctx!.strokeStyle = this.color;
        ctx!.lineWidth = 2;
        ctx!.lineCap = 'round';
        ctx!.shadowBlur = 15;
        ctx!.shadowColor = this.color;

        for (let x = 0; x < w; x += 2) {
          const y = h / 2 + Math.sin(x * this.frequency + this.phase + this.offset) * this.amplitude * Math.sin(this.phase * 0.5);
          if (x === 0) ctx!.moveTo(x, y);
          else ctx!.lineTo(x, y);
        }
        ctx!.stroke();
        this.phase += this.speed;
      }
    }

    const waves = [
      new Wave(0, 'rgba(139, 92, 246, 0.3)', 150, 0.02),
      new Wave(Math.PI / 2, 'rgba(99, 102, 241, 0.2)', 100, 0.015),
      new Wave(Math.PI, 'rgba(167, 139, 250, 0.15)', 180, 0.01)
    ];

    const render = () => {
      ctx.clearRect(0, 0, w, h);
      waves.forEach(wave => wave.draw());
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await authService.login({ email, password });
      if (response.status) {
        await login(response.data.token);
        const base64Url = response.data.token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(base64));
        
        if (payload.admin === 1) {
          navigate('/admin/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(response.message || 'Login failed. Please check your details.');
      }
    } catch (err: any) {
      setError(err.message || 'Error connecting to server.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-950 px-6 py-12 font-sans relative overflow-hidden">
      {/* Electric Wave Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none opacity-50" />

      {/* Cinematic Background Glows */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse"></div>
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none animate-pulse delay-700"></div>

      <div className="max-w-md w-full glass-panel p-1 border-transparent relative rounded-[2.5rem] group overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-1000">
        {/* Animated Border Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-transparent to-indigo-600 opacity-20 group-hover:opacity-40 transition-opacity duration-1000"></div>
        
        <div className="bg-slate-950/90 backdrop-blur-3xl p-10 lg:p-12 rounded-[2.4rem] relative z-10 scanline-effect overflow-hidden">
          <div className="text-center">
            {/* AICW Hex-Core Logo */}
            <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 mb-6 shadow-2xl shadow-purple-600/20 animate-in spin-in-12 duration-1000">
              <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
                <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
                <circle cx="12" cy="13.5" r="1.2" fill="white" className="animate-pulse" />
                <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            
            <div className="animate-in slide-in-from-top-4 duration-1000 delay-200">
              <h1 className="text-3xl font-black text-white tracking-tight uppercase">AICryptoWorlds</h1>
              <p className="text-[10px] text-purple-400 font-bold uppercase tracking-[0.2em] mt-2 mb-8">Your Gateway to the Future of Assets</p>
            </div>
            
            <div className="h-[1px] w-12 bg-slate-800 mx-auto mb-8"></div>
            
            <div className="animate-in slide-in-from-bottom-2 duration-1000 delay-400">
              <h2 className="text-xl font-bold text-slate-300 tracking-tight">Sign In</h2>
              <p className="text-xs text-slate-500 mt-1">Access your secure trading terminal</p>
            </div>
          </div>
          
          {error && (
            <div className="mt-8 bg-red-500/10 border border-red-500/20 text-red-500 text-[11px] p-4 rounded-2xl text-center font-black uppercase tracking-widest animate-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <form className="space-y-6 mt-10" onSubmit={handleSubmit}>
            <div className="space-y-5">
              <div className="group animate-in slide-in-from-left-6 duration-1000 delay-500">
                <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Email Address</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
                  placeholder="identity@aicw.net"
                />
              </div>
              <div className="group animate-in slide-in-from-right-6 duration-1000 delay-600">
                <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none pr-14 font-bold placeholder-slate-700 text-sm"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-purple-400 transition-colors p-2"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268-2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end pr-1">
              <Link to="/forgot-password" university-link className="text-[10px] font-black text-purple-500 hover:text-purple-400 uppercase tracking-widest transition-colors">
                Forgot Password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-4 px-6 bg-purple-600 hover:bg-purple-500 text-white font-black rounded-2xl transition-all shadow-2xl shadow-purple-600/30 active:scale-95 disabled:opacity-50 uppercase tracking-[0.2em] text-xs"
            >
              {isSubmitting ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>

          <div className="space-y-6 pt-10 border-t border-white/5 mt-10">
            <p className="text-center text-[10px] text-slate-500 font-bold uppercase tracking-widest">
              New to AICW?{' '}
              <Link to="/register" className="text-purple-500 hover:text-purple-400 transition-colors ml-1">
                Create Account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;